// DESIGN_SYSTEM_NOTES: Single source of truth for the in-app navigation tree.
// Both Sidebar and the dashboard's "Sezioni" grid render from `navSections`.
//
// Visibility rule (applied centrally by `visibleNavSections`):
//   1. Drop a section the role can't access.
//   2. Drop a section whose `module` gate isn't enabled (SUPER_ADMIN bypasses).
//   3. Drop items the role can't access or whose `module` isn't enabled.
//   4. Drop a section that ends up with zero visible items.
//
// `roles` may live on the section AND on each item — item-level wins for the
// "hide what the user can't actually open" rule. Routes that are SUPER_ADMIN-
// only must mark their item `roles: ['SUPER_ADMIN']` to stay consistent with
// ProtectedRoute.
//
// Subgroups (Sidebar only): items may carry an optional `group` label.
// The Sidebar inserts a thin divider header whenever the group changes from
// the previous *visible* item — that visually chunks long sections (Workforce,
// Configurazione, Amministrazione) without changing any route, role gate, or
// the HomeDashboard "Sezioni" tile (which still treats each section as one
// unit and lands on `items[0]`). Order matters: list items in their intended
// subgroup order; consecutive items sharing a `group` value collapse under
// one header. Items without a `group` appear ungrouped at the top.

import { ROLE_HIERARCHY as ROLE_LEVEL, moduliProduzione } from './rbacRoles.js';
import {
  LayoutDashboard,
  ClipboardList,
  BarChart3,
  Users,
  Shield,
  Palette,
  Mail,
  ScrollText,
  Trophy,
  Send,
  Database,
  MessageCircle,
  LifeBuoy,
  Briefcase,
  Home,
  SlidersHorizontal,
  BookUser,
  Sparkles,
  UserCheck,
  Newspaper,
} from 'lucide-react';

// Each section has its own `icon` (rendered as the big tile on the HomeDashboard
// "Sezioni" grid) in addition to per-item icons. Section icons are independent
// of the items — picking one based on the section's identity keeps the home grid
// readable even when item icons are mixed (e.g. Workforce).
// Una voce di menu può raccogliere più pagine in **schede**: il menu si
// accorcia, le pagine restano dove sono.
//
// La regola che tiene tutto insieme: il permesso resta per **pagina**, non per
// voce. Il catalogo (packages/rbac-core/permessi.js) ha un codice per ogni
// percorso, e continua ad averlo: una voce raggruppata compare se almeno una
// delle sue schede è permessa, e dentro la pagina si vedono **solo** le schede
// permesse. Chi ha diritto solo alle Ferie apre «Il mio lavoro» e trova le
// Ferie: non le altre quattro schede, non un menu più corto ma più permissivo.
//
// Ogni scheda conserva il proprio indirizzo: i segnalibri, i link nelle
// notifiche e le email già spedite continuano a funzionare.
export const navSections = [
  {
    label: 'Home',
    icon: Home,
    roles: ['OPERATOR', 'ADMIN', 'MANAGER', 'SUPER_ADMIN'],
    items: [
      { to: '/home', icon: Home, label: 'Home' },
    ],
  },
  {
    label: 'Operatore',
    icon: LayoutDashboard,
    roles: ['OPERATOR', 'ADMIN', 'SUPER_ADMIN'],
    items: [
      {
        to: '/dashboard',
        icon: LayoutDashboard,
        label: 'Produzione',
        schede: [
          { to: '/dashboard', label: 'Dashboard', requireMultipleProductionModules: true },
          { to: '/pratiche', label: 'Pratiche' },
        ],
      },
    ],
  },
  {
    // No `module` key: news is the platform's front page, not a module a user
    // is assigned to. Every role sees the section; what each person can read
    // inside it is decided per item by its audience.
    label: 'NEWS',
    icon: Newspaper,
    roles: ['OPERATOR', 'ADMIN', 'MANAGER', 'SUPER_ADMIN'],
    items: [
      { to: '/news', icon: Newspaper, label: 'Prima pagina' },
    ],
  },
  {
    label: 'EFFEISBUCK',
    icon: MessageCircle,
    roles: ['OPERATOR', 'ADMIN', 'MANAGER', 'SUPER_ADMIN'],
    module: 'effeisbuck',
    items: [
      {
        to: '/effeisbuck',
        icon: MessageCircle,
        label: 'Bacheca',
        module: 'effeisbuck',
        schede: [
          { to: '/effeisbuck', label: 'Bacheca', module: 'effeisbuck' },
          { to: '/effeisbuck/profilo', label: 'Mio profilo', module: 'effeisbuck' },
        ],
      },
    ],
  },
  {
    label: 'HELPDESK',
    icon: LifeBuoy,
    roles: ['OPERATOR', 'ADMIN', 'MANAGER', 'SUPER_ADMIN'],
    module: 'helpdesk',
    items: [
      {
        to: '/helpdesk',
        icon: LifeBuoy,
        label: 'Ticket',
        module: 'helpdesk',
        // Coda e board sono due modi di guardare la stessa coda: una voce
        // sola, due schede, e la scelta resta di chi lavora.
        schede: [
          { to: '/helpdesk', label: 'Coda', module: 'helpdesk' },
          { to: '/helpdesk/board', label: 'Board', module: 'helpdesk' },
          { to: '/helpdesk/stats', label: 'Statistiche', module: 'helpdesk' },
          { to: '/helpdesk/settings', label: 'Impostazioni', module: 'helpdesk', roles: ['SUPER_ADMIN'] },
        ],
      },
    ],
  },
  {
    label: 'Rubrica',
    icon: BookUser,
    roles: ['OPERATOR', 'ADMIN', 'MANAGER', 'SUPER_ADMIN'],
    items: [
      // One phonebook per module; the page itself switches between the user's
      // accessible module phonebooks. `requireAnyModule` hides the item for a
      // non-SUPER_ADMIN who has no modules assigned (nothing to show).
      { to: '/rubrica', icon: BookUser, label: 'Rubrica', requireAnyModule: true },
    ],
  },
  {
    label: 'Amministrazione',
    icon: BarChart3,
    roles: ['ADMIN', 'MANAGER', 'SUPER_ADMIN'],
    items: [
      { to: '/admin/leaderboard', icon: Trophy, label: 'Classifica', roles: ['ADMIN', 'SUPER_ADMIN'] },
      {
        to: '/admin/analisi',
        icon: Database,
        label: 'Dati e moduli',
        roles: ['ADMIN', 'SUPER_ADMIN'],
        schede: [
          { to: '/admin/analisi', label: 'Analisi Dati', roles: ['ADMIN', 'SUPER_ADMIN'] },
          { to: '/admin/moduli', label: 'Moduli', roles: ['ADMIN', 'SUPER_ADMIN'] },
          { to: '/admin/archivio', label: 'Archivio', roles: ['ADMIN', 'SUPER_ADMIN'] },
        ],
      },
    ],
  },
  {
    label: 'Workforce',
    icon: Briefcase,
    roles: ['OPERATOR', 'ADMIN', 'MANAGER', 'SUPER_ADMIN'],
    items: [
      {
        to: '/workforce/presenze',
        icon: ClipboardList,
        label: 'Il mio lavoro',
        group: 'Personale',
        schede: [
          { to: '/workforce/presenze', label: 'Presenze' },
          { to: '/workforce/ferie', label: 'Ferie' },
          { to: '/workforce/saldi', label: 'Saldi' },
          { to: '/workforce/banca-ore', label: 'Banca ore' },
          { to: '/workforce/festivita', label: 'Festività' },
        ],
      },
      {
        to: '/workforce/presenze-team',
        icon: UserCheck,
        label: 'Gestione team',
        group: 'Gestione',
        roles: ['ADMIN', 'MANAGER', 'SUPER_ADMIN'],
        schede: [
          { to: '/workforce/presenze-team', label: 'Presenze Team', roles: ['ADMIN', 'MANAGER', 'SUPER_ADMIN'] },
          { to: '/workforce/blocchi', label: 'Blocchi operativi', roles: ['ADMIN', 'MANAGER', 'SUPER_ADMIN'] },
          { to: '/workforce/gruppi', label: 'Gruppi', roles: ['ADMIN', 'MANAGER', 'SUPER_ADMIN'] },
          { to: '/workforce/costo-lavoro', label: 'Costo del lavoro', roles: ['ADMIN', 'MANAGER', 'SUPER_ADMIN'] },
        ],
      },
      {
        to: '/workforce/ccnl',
        icon: ScrollText,
        label: 'Contratti',
        group: 'Gestione',
        roles: ['MANAGER', 'SUPER_ADMIN'],
        schede: [
          { to: '/workforce/ccnl', label: 'Regole CCNL', roles: ['MANAGER', 'SUPER_ADMIN'] },
          { to: '/workforce/maturazioni', label: 'Maturazioni', roles: ['MANAGER', 'SUPER_ADMIN'] },
        ],
      },
      // Le notifiche broadcast parlano alle persone dell'azienda: stanno con
      // chi le persone le gestisce, non fra i dati e i moduli. L'indirizzo
      // resta /admin/notifiche — la pagina non si è spostata, solo la voce.
      { to: '/admin/notifiche', icon: Send, label: 'Notifiche', group: 'Gestione', roles: ['SUPER_ADMIN'] },
    ],
  },
  {
    label: 'IAM',
    icon: Shield,
    // Widened to ADMIN+ on Persone e permessi / Gestione Utenti because ADMIN
    // holds `azione.iam.utenti.assenze` by role (`implicaViste: ['vista.iam.utenti']`).
    // All sibling tabs keep their stricter MANAGER / SUPER_ADMIN thresholds.
    roles: ['ADMIN', 'MANAGER', 'SUPER_ADMIN'],
    items: [
      {
        to: '/iam/utenti',
        icon: Users,
        label: 'Persone e permessi',
        roles: ['ADMIN', 'MANAGER', 'SUPER_ADMIN'],
        schede: [
          { to: '/iam/utenti', label: 'Gestione Utenti', roles: ['ADMIN', 'MANAGER', 'SUPER_ADMIN'] },
          { to: '/iam/ruoli', label: 'Ruoli', roles: ['MANAGER', 'SUPER_ADMIN'] },
          { to: '/iam/moduli', label: 'Registro Moduli', roles: ['MANAGER', 'SUPER_ADMIN'] },
          { to: '/iam/permessi', label: 'Profili e permessi', roles: ['SUPER_ADMIN'] },
          { to: '/iam/gadget', label: 'Gadget aziendali', roles: ['SUPER_ADMIN'] },
        ],
      },
      {
        to: '/iam/sessioni',
        icon: ScrollText,
        label: 'Registri',
        roles: ['MANAGER', 'SUPER_ADMIN'],
        schede: [
          { to: '/iam/sessioni', label: 'Sessioni', roles: ['MANAGER', 'SUPER_ADMIN'] },
          { to: '/iam/audit', label: 'Audit', roles: ['MANAGER', 'SUPER_ADMIN'] },
          { to: '/iam/email', label: 'Email', roles: ['SUPER_ADMIN'] },
        ],
      },
    ],
  },
  {
    label: 'Configurazione',
    icon: SlidersHorizontal,
    roles: ['MANAGER', 'SUPER_ADMIN'],
    items: [
      {
        to: '/config/branding',
        icon: Palette,
        label: 'Aspetto',
        roles: ['SUPER_ADMIN'],
        schede: [
          { to: '/config/branding', label: 'Branding', roles: ['SUPER_ADMIN'] },
          { to: '/config/email-templates', label: 'Template Email', roles: ['SUPER_ADMIN'] },
        ],
      },
      {
        to: '/config/smtp',
        icon: Mail,
        label: 'Comunicazione',
        roles: ['SUPER_ADMIN'],
        schede: [
          { to: '/config/smtp', label: 'Email SMTP', roles: ['SUPER_ADMIN'] },
          { to: '/config/webrtc', label: 'WebRTC Telefono', roles: ['SUPER_ADMIN'] },
          { to: '/config/webhooks', label: 'Webhooks', roles: ['SUPER_ADMIN'] },
        ],
      },
      {
        to: '/config/security',
        icon: Shield,
        label: 'Sicurezza e sistema',
        roles: ['SUPER_ADMIN'],
        schede: [
          { to: '/config/security', label: 'Sicurezza', roles: ['SUPER_ADMIN'] },
          { to: '/config/rate-limits', label: 'Rate Limit', roles: ['SUPER_ADMIN'] },
          { to: '/config/data-retention', label: 'Data Retention', roles: ['SUPER_ADMIN'] },
          { to: '/config/system', label: 'Stato Sistema', roles: ['SUPER_ADMIN'] },
          { to: '/config/sso', label: 'Single Sign-On', roles: ['SUPER_ADMIN'] },
        ],
      },
      { to: '/config/ai', icon: Sparkles, label: 'Intelligenza Artificiale', roles: ['SUPER_ADMIN'] },
    ],
  },
];

// Role hierarchy — sourced from rbacRoles.js (generated from the platform's
// shared rbac-core, see scripts/sync-rbac-core.js) so the nav, `ProtectedRoute`
// (frontend router), and `authorize(role)` on the backend all read from the
// same values instead of three independently hand-copied objects.
export { ROLE_LEVEL };

/**
 * Hierarchical role check. A `roles` array on a section/item is read as the
 * SET of roles whose minimum level grants access — so `['ADMIN', 'SUPER_ADMIN']`
 * means "ADMIN or higher" (ADMIN, MANAGER, SUPER_ADMIN), matching what the
 * FE router (`ProtectedRoute`) and BE `authorize()` already do. Empty / missing
 * `roles` ⇒ no role restriction (visible to anyone authenticated).
 *
 * Pre-2026-06 this was an exact-match (`roles.includes(ruolo)`), which left
 * MANAGER seeing fewer items than they could actually open by URL — the BE
 * already allowed MANAGER+ on the gated routes. The hierarchical reading
 * closes that cross-layer drift.
 */
export function passesRoles(declaredRoles, ruolo) {
  if (!Array.isArray(declaredRoles) || declaredRoles.length === 0) return true;
  const userLevel = ROLE_LEVEL[ruolo] || 0;
  const minLevel = Math.min(
    ...declaredRoles.map((r) => ROLE_LEVEL[r] ?? Number.MAX_SAFE_INTEGER)
  );
  return userLevel >= minLevel;
}

export { moduliProduzione };

/**
 * Compute the navigation tree visible to a given user.
 * Drops sections the role/module can't access, drops disallowed items, then
 * drops sections that ended up empty so the UI never renders a header with
 * no clickable rows under it.
 */
export function visibleNavSections(user, accesso = null) {
  const permessi = accesso && Array.isArray(accesso.permessi) ? accesso.permessi : null;
  const perPercorso = accesso && Array.isArray(accesso.catalogo)
    ? Object.fromEntries(accesso.catalogo.map((v) => [v.percorso, v.codice]))
    : {};
  const ruolo = user?.ruolo || 'OPERATOR';
  const userMods = user?.moduli_abilitati || [];
  const isSuper = ruolo === 'SUPER_ADMIN';

  // Quando i permessi sono stati letti, decidono loro.
  //
  // Prima il ruolo scritto qui e il permesso del catalogo erano due cancelli
  // in serie, e il primo ignorava la matrice: a chi riceveva un permesso su
  // una pagina sopra il proprio ruolo la voce restava invisibile. È successo
  // davvero — cinque permessi su Gestione Utenti assegnati a un ADMIN, e il
  // menu IAM che non compariva, perché `vista.iam.utenti` è da MANAGER in su.
  //
  // Non è un allargamento: `ruoloMinimo` nel catalogo porta le stesse soglie
  // che sono scritte qui, quindi per chi non ha profili né eccezioni il menu
  // risulta identico (lo verificano i test che confrontano menu e catalogo
  // ruolo per ruolo). Il `roles` resta la regola finché i permessi non sono
  // arrivati, o se la lettura fallisce: un errore di rete non deve svuotare
  // il menu a nessuno.
  const decidonoIPermessi = Boolean(permessi) && !isSuper;

  // Un percorso è raggiungibile se lo consentono ruolo (o permesso) e modulo.
  // È la stessa domanda per una voce e per una scheda: il permesso vive sul
  // percorso, e raggruppare le voci non lo allarga né lo restringe.
  const consentito = (voce) => {
    const codice = perPercorso[voce.to];
    if (decidonoIPermessi && codice) {
      if (!permessi.includes(codice)) return false;
    } else if (voce.roles && !passesRoles(voce.roles, ruolo)) {
      return false;
    }
    if (voce.module && !isSuper && !userMods.includes(voce.module)) return false;
    if (voce.requireAnyModule && !isSuper && userMods.length === 0) return false;
    if (voce.requireMultipleProductionModules && !isSuper && moduliProduzione(userMods).length <= 1) return false;
    return true;
  };

  return navSections
    // La sezione non si filtra più sul ruolo quando decidono i permessi: le
    // voci sanno già chi le può aprire, e una sezione senza voci sparisce
    // comunque in fondo a questa catena.
    .filter((section) => decidonoIPermessi || passesRoles(section.roles, ruolo))
    .filter((section) => !section.module || isSuper || userMods.includes(section.module))
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => {
          if (!item.schede) return consentito(item) ? item : null;
          // Una voce raggruppata vale quanto le sue schede permesse: se non ne
          // resta nessuna sparisce, e se ne resta una sola il menu porta
          // direttamente lì.
          const schede = item.schede.filter(consentito);
          if (schede.length === 0) return null;
          return { ...item, schede, to: schede[0].to };
        })
        .filter(Boolean),
    }))
    .filter((section) => section.items.length > 0);
}

// Tutti i percorsi che una voce copre: serve al menu per sapere quando
// evidenziarsi, e ai test per verificare che accorpando non si sia perso né
// aggiunto niente.
export function percorsiDiVoce(item) {
  return item.schede ? item.schede.map((s) => s.to) : [item.to];
}

// Le schede da mostrare in cima a una pagina raggruppata. Chi chiama passa il
// percorso corrente: la voce si ritrova da sola, e le schede sono già
// filtrate su ruolo, moduli e permessi.
export function schedeDiPercorso(percorso, user, accesso = null) {
  for (const sezione of visibleNavSections(user, accesso)) {
    for (const item of sezione.items) {
      if (!item.schede) continue;
      if (item.schede.some((s) => s.to === percorso || percorso.startsWith(`${s.to}/`))) {
        return item.schede;
      }
    }
  }
  return [];
}

// `accesso` è { permessi, catalogo } come arrivano da usePermessiStore. La
// corrispondenza voce → codice passa dal percorso: il catalogo vive nel
// backend (packages/rbac-core/permessi.js), qui non si ricopia nessun elenco.
